import { queryOptions } from '@tanstack/react-query'

import { BASE_API_URL } from 'core/infra/environment'
import { HttpClient } from 'core/infra/http/HttpClient'
import { SentryWrapper } from 'core/infra/sentry/SentryWrapper'
import { PBIRepository } from 'managed-analytics/infra/PBIRepository'

export type GetPBIReportsFactoryDependencies = {
  pbiRepository: PBIRepository
}

export type GetPBIExportableReportsFactoryDependencies = {
  pbiRepository: PBIRepository
}

export type GetPBIReportFactoryDependencies = {
  pbiRepository: PBIRepository
}

export type GetPBIDemoReportsFactoryDependencies = {
  pbiRepository: PBIRepository
}

export type GetPBIDemoReportFactoryDependencies = {
  pbiRepository: PBIRepository
}

export function getPBIReportsFactory({ pbiRepository }: GetPBIReportsFactoryDependencies) {
  return async function (config?: { signal?: AbortSignal }) {
    try {
      const token = await pbiRepository.getReports(config)
      return token
    } catch (error) {
      SentryWrapper.log(error as Error)
      throw error
    }
  }
}

export function getPBIReportFactory({ pbiRepository }: GetPBIReportFactoryDependencies) {
  return async function (reportId: string, config?: { signal?: AbortSignal }) {
    try {
      const response = await pbiRepository.getReport(reportId, config)
      return response
    } catch (error) {
      SentryWrapper.log(error as Error)
      throw error
    }
  }
}

export function getPBIDemoReportsFactory({ pbiRepository }: GetPBIDemoReportsFactoryDependencies) {
  return async function (config?: { signal?: AbortSignal }) {
    try {
      const token = await pbiRepository.getDemoReports(config)
      return token
    } catch (error) {
      SentryWrapper.log(error as Error)
      throw error
    }
  }
}

export function getPBIDemoReportFactory({ pbiRepository }: GetPBIDemoReportFactoryDependencies) {
  return async function (reportId: string, config?: { signal?: AbortSignal }) {
    try {
      const response = await pbiRepository.getDemoReport(reportId, config)
      return response
    } catch (error) {
      SentryWrapper.log(error as Error)
      throw error
    }
  }
}

export function getPBIExportableReportsFactory({
  pbiRepository,
}: GetPBIExportableReportsFactoryDependencies) {
  return async function (config?: { signal?: AbortSignal }) {
    try {
      const token = await pbiRepository.getExportableReports(config)
      return token
    } catch (error) {
      SentryWrapper.log(error as Error)
      throw error
    }
  }
}

export function getPBIReports(config?: { signal?: AbortSignal }) {
  const httpClient = new HttpClient(BASE_API_URL)
  const accountRepository = new PBIRepository({ httpClient })

  const get = getPBIReportsFactory({ pbiRepository: accountRepository })

  return get(config)
}

export function getPBIReport(reportId: string, config?: { signal?: AbortSignal }) {
  const httpClient = new HttpClient(BASE_API_URL)
  const accountRepository = new PBIRepository({ httpClient })

  const get = getPBIReportFactory({ pbiRepository: accountRepository })

  return get(reportId, config)
}

export function getPBIDemoReports(config?: { signal?: AbortSignal }) {
  const httpClient = new HttpClient(BASE_API_URL)
  const accountRepository = new PBIRepository({ httpClient })

  const get = getPBIDemoReportsFactory({ pbiRepository: accountRepository })

  return get(config)
}

export function getPBIDemoReport(reportId: string, config?: { signal?: AbortSignal }) {
  const httpClient = new HttpClient(BASE_API_URL)
  const accountRepository = new PBIRepository({ httpClient })

  const get = getPBIDemoReportFactory({ pbiRepository: accountRepository })

  return get(reportId, config)
}

export function getPBIExportableReports(config?: { signal?: AbortSignal }) {
  const httpClient = new HttpClient(BASE_API_URL)
  const accountRepository = new PBIRepository({ httpClient })

  const get = getPBIExportableReportsFactory({ pbiRepository: accountRepository })

  return get(config)
}

export function getPBIReportsQueryOptions() {
  return queryOptions({
    queryKey: ['pbiReports'],
    queryFn: () => getPBIReports(),
  })
}

export function getPBIReportQueryOptions(reportId: string) {
  return queryOptions({
    queryKey: ['pbiReport', reportId],
    queryFn: () => getPBIReport(reportId),
  })
}

export function getPBIDemoReportsQueryOptions() {
  return queryOptions({
    queryKey: ['pbiDemoReports'],
    queryFn: () => getPBIDemoReports(),
  })
}

export function getPBIDemoReportQueryOptions(reportId: string) {
  return queryOptions({
    queryKey: ['pbiDemoReport', reportId],
    queryFn: () => getPBIDemoReport(reportId),
  })
}
